import React, { useState, useEffect, useCallback } from 'react';
import {
  useLinesContext,
  useSnackbarContext,
  useEventTrackerContext,
} from '../../../../contexts';
import { ILine as Line, IStage as Stage } from '../../../../global';
import {
  addOrDeleteLineEditError,
  addStage,
  handleEditStage,
  displayLineEditErrors,
  stage as stageEnum,
} from '../../../../utils';
import messages from '../../../../messages';
import Dialog from '../../Dialogs/DialogStyleTwo/Dialog';
import MobileLine from './MobileLine';
import Button from '@material-ui/core/Button';
import LoadingBackdrop from '../../../LoadingBackdrop';

interface Props {
  line: Line;
}

export default function MobileEditLineDialog({ line }: Props) {
  const snackbar = useSnackbarContext();
  const { trackEvent } = useEventTrackerContext();
  const { lines, editLineCancel, editLineSubmit } = useLinesContext();
  const [editableLine, setEditableLine] = useState<Line>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const HEADER = line?.new ? 'Create Line' : 'Edit Line';

  const setLine = useCallback(() => {
    if (line && !editableLine) {
      setEditableLine(line);
    }
    if (!line) {
      setEditableLine(null);
    }
  }, [line, editableLine]);

  useEffect(setLine, [line]);

  let updatedLineErrors = {};
  let updatedLineInfo = {};

  function enterStageEditMode(stageId: string, edit: boolean) {
    const updatedStages = line.stages.map((stage: Stage) => {
      if (stage.id === stageId) {
        stage.edit = edit;
      } else {
        if (edit) {
          stage.edit = false;
        }
      }
      return stage;
    });
    setEditableLine({ ...line, stages: updatedStages });
  }

  function updateLineInfo(key: string, value: number) {
    updatedLineInfo[key] = value;
  }

  const handleStageUpdate = (
    id: string,
    key: string,
    value: string | number | boolean,
    useDebounce: boolean = false,
  ) => {
    if (key === 'edit') {
      enterStageEditMode(id, value as boolean);
    } else {
      handleEditStage(
        line,
        setEditableLine,
        id,
        key,
        value,
        useDebounce,
        snackbar,
        true,
      );
    }
  };

  function handleAddStage(stageName: string) {
    addStage(stageName, line, setEditableLine, snackbar);
  }

  function addOrDeleteError(
    addError: boolean = true,
    errKey: string,
    error?: string,
  ) {
    addOrDeleteLineEditError(updatedLineErrors, addError, errKey, error);
  }

  function verifyMobileLine(line: Line) {
    line?.stages.forEach((stage: Stage) => {
      if (stage.name !== stageEnum.sellingStart.value && !stage.deleted) {
        if (!stage.amount || isNaN(stage.amount)) {
          addOrDeleteError(
            true,
            'stage.' + stage.id + '.amount',
            messages.lines.lineEditErrors.stageAmountValidNum,
          );
        }
      }
    });
  }

  async function handleSubmit() {
    setLoading(true);
    try {
      const newLine = { ...editableLine, ...updatedLineInfo };
      setEditableLine(newLine);
      verifyMobileLine(newLine);
      const errorDisplayed = displayLineEditErrors(
        newLine,
        updatedLineErrors,
        snackbar,
      );
      if (!errorDisplayed) {
        const lineId = await editLineSubmit(newLine);
        const successMessage = newLine?.new
          ? messages.lines.createLineSuccess
          : messages.lines.success(newLine.name, 'edited');
        snackbar.show(successMessage, 'success');
        trackEvent({
          event: line.new ? 'line_added' : 'line_edited',
          total_lines: lines.length,
          line: { ...newLine, id: lineId },
        });
      }
    } catch (error) {
      let errorMessage = '';
      if (error?.message === 'INVALID_LINE_LENGTH') {
        errorMessage = messages.lines.invalidLineLength;
      }
      if (error?.message === 'LINE_NAME_NOT_UNIQUE') {
        errorMessage = messages.lines.lineEditErrors.nameNotUnique;
      }
      if (!errorMessage) {
        errorMessage =
          messages.lines[line?.new ? 'createLineError' : 'editLineError'];
      }
      snackbar.show(errorMessage, 'error', 10000);
    }
    setLoading(false);
  }

  return (
    <>
      <LoadingBackdrop open={loading} />
      <Dialog
        open={!!line}
        onClose={() => editLineCancel(line?.id)}
        header={HEADER}
        actions={
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Save
          </Button>
        }
      >
        <MobileLine
          {...{
            line: editableLine,
            handleAddStage,
            handleStageUpdate,
            addOrDeleteError,
            updateLineInfo,
          }}
          startExpand={true}
        />
      </Dialog>
    </>
  );
}
